import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { apiGet, apiPost } from "../../utils/api";
import { useLanguage } from "../../contexts/LanguageContext";
import { AdminFormDialog } from "./AdminFormDialog";
import { Colors } from "../../utils/utils.colors";

type Props = {
  open: boolean;
  content: any | null;
  onClose: () => void;
};

export const AdminBroadcastModal: React.FC<Props> = ({ open, content, onClose }) => {
  const { t } = useLanguage();
  const [subscribers, setSubscribers] = useState<any[]>([]);
  const [mode, setMode] = useState<"all" | "select">("all");
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    apiGet("/newsletter")
      .then((d) => {
        setSubscribers(d.rows || []);
        setSelected(new Set());
        setMode("all");
      })
      .catch(() => toast.error(t.common.error))
      .finally(() => setLoading(false));
  }, [open, t.common.error]);

  const toggleSelect = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    setSelected(new Set(subscribers.map((s) => s.id)));
  };

  const deselectAll = () => {
    setSelected(new Set());
  };

  const send = async () => {
    if (!content) return;
    const recipientIds = mode === "all"
      ? subscribers.map((s) => s.id)
      : Array.from(selected);

    if (recipientIds.length === 0) {
      toast.warning(t.admin.broadcastNoRecipients || "Aucun destinataire sélectionné");
      return;
    }

    setSending(true);
    try {
      await apiPost("/newsletter/broadcast", {
        contentId: content.id,
        recipientIds,
        mode,
      });
      toast.success(t.admin.broadcastSent || "Diffusion envoyée avec succès");
      onClose();
    } catch (e: any) {
      toast.error(e?.response?.data?.reason || e?.messageText || t.common.error);
    } finally {
      setSending(false);
    }
  };

  if (!content) return null;

  return (
    <AdminFormDialog
      open={open}
      onClose={onClose}
      title={t.admin.broadcastTitle || "Diffuser la publication"}
      subtitle={t.admin.broadcastSubtitle || "Envoyer un e-mail aux abonnés de la newsletter"}
      testId="broadcast-modal"
      className="max-w-2xl"
      submitLabel={sending ? (t.admin.broadcastSending || "Envoi…") : (t.admin.broadcastSend || "Envoyer")}
      isSubmitting={sending}
      onSubmitClick={send}
    >
      <div style={{ padding: "0 0 16px" }}>
        {/* Content preview */}
        <div style={{
          background: "#f8fafc", borderRadius: 12, padding: 16, marginBottom: 20,
          border: "1px solid #e2e8f0", display: "flex", gap: 16, alignItems: "center",
        }}>
          {content.coverImage && (
            <img src={content.coverImage} alt="" style={{ width: 80, height: 60, objectFit: "cover", borderRadius: 8, flexShrink: 0 }} />
          )}
          <div>
            <div style={{ fontSize: 11, color: Colors.redColor, textTransform: "uppercase", fontWeight: 700 }}>
              {content.type}
            </div>
            <h4 style={{ margin: "4px 0 0", fontSize: 15, color: Colors.darkColor }}>{content.title}</h4>
          </div>
        </div>

        {/* Mode selector */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ fontWeight: 600, fontSize: 14, color: Colors.darkColor, display: "block", marginBottom: 10 }}>
            {t.admin.broadcastRecipients || "Destinataires"}
          </label>
          <div style={{ display: "flex", gap: 10 }}>
            <button
              type="button"
              onClick={() => setMode("all")}
              style={{
                flex: 1, padding: "12px 16px", borderRadius: 10, border: `2px solid ${mode === "all" ? Colors.primaryColor : "#e2e8f0"}`,
                background: mode === "all" ? "#eef0ff" : "#fff", cursor: "pointer",
                color: mode === "all" ? Colors.primaryColor : "#64748b", fontWeight: 600, fontSize: 14,
                transition: "all .2s",
              }}
            >
              <div style={{ fontSize: 20, marginBottom: 4 }}>&#128227;</div>
              {t.admin.broadcastAll || "Tous les abonnés"}
              <div style={{ fontSize: 12, fontWeight: 400, marginTop: 4, opacity: 0.7 }}>
                {subscribers.length} {t.admin.broadcastSubscribers || "abonné(s)"}
              </div>
            </button>
            <button
              type="button"
              onClick={() => setMode("select")}
              style={{
                flex: 1, padding: "12px 16px", borderRadius: 10, border: `2px solid ${mode === "select" ? Colors.primaryColor : "#e2e8f0"}`,
                background: mode === "select" ? "#eef0ff" : "#fff", cursor: "pointer",
                color: mode === "select" ? Colors.primaryColor : "#64748b", fontWeight: 600, fontSize: 14,
                transition: "all .2s",
              }}
            >
              <div style={{ fontSize: 20, marginBottom: 4 }}>&#9745;</div>
              {t.admin.broadcastSelect || "Sélectionner"}
              <div style={{ fontSize: 12, fontWeight: 400, marginTop: 4, opacity: 0.7 }}>
                {t.admin.broadcastSelectDesc || "Choisir les destinataires"}
              </div>
            </button>
          </div>
        </div>

        {/* Subscriber list (for select mode) */}
        {mode === "select" && (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <span style={{ fontSize: 13, color: "#64748b" }}>
                {selected.size} / {subscribers.length} {t.admin.broadcastSelected || "sélectionné(s)"}
              </span>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  type="button"
                  onClick={selectAll}
                  style={{ fontSize: 12, color: Colors.primaryColor, background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}
                >
                  {t.admin.broadcastSelectAll || "Tout sélectionner"}
                </button>
                <button
                  type="button"
                  onClick={deselectAll}
                  style={{ fontSize: 12, color: "#94a3b8", background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}
                >
                  {t.admin.broadcastDeselectAll || "Tout désélectionner"}
                </button>
              </div>
            </div>
            <div style={{
              maxHeight: 240, overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: 10,
              background: "#fff",
            }}>
              {loading && (
                <div style={{ padding: 20, textAlign: "center", color: "#94a3b8" }}>{t.common.loading}</div>
              )}
              {!loading && subscribers.length === 0 && (
                <div style={{ padding: 20, textAlign: "center", color: "#94a3b8" }}>
                  {t.admin.emptyNewsletter}
                </div>
              )}
              {!loading && subscribers.map((sub) => (
                <label
                  key={sub.id}
                  style={{
                    display: "flex", alignItems: "center", gap: 12, padding: "10px 16px",
                    borderBottom: "1px solid #f1f5f9", cursor: "pointer",
                    background: selected.has(sub.id) ? "#f0f4ff" : "transparent",
                    transition: "background .15s",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selected.has(sub.id)}
                    onChange={() => toggleSelect(sub.id)}
                    style={{ width: 16, height: 16, accentColor: Colors.primaryColor }}
                  />
                  <span style={{ fontSize: 14, color: Colors.darkColor }}>{sub.email}</span>
                  <span style={{ fontSize: 11, color: "#94a3b8", marginLeft: "auto" }}>
                    {new Date(sub.createdAt).toLocaleDateString("fr-FR")}
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}

        {mode === "all" && !loading && subscribers.length > 0 && (
          <div style={{
            background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 10,
            padding: "14px 18px", fontSize: 14, color: "#166534", display: "flex", alignItems: "center", gap: 10,
          }}>
            <span style={{ fontSize: 18 }}>&#9989;</span>
            {t.admin.broadcastAllConfirm || `L'e-mail sera envoyé à ${subscribers.length} abonné(s).`}
          </div>
        )}
      </div>
    </AdminFormDialog>
  );
};
